import * as React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import Localization from '../models/Localization';
import ShopItemDto, { ShopDisplaySection } from '../models/dtos/ShopItemDto';
import {
    injectShopHtml,
    localizedFirstPurchaseEyebrow,
    shopHtmlCardHeight,
    ShopHtmlPreviewContext,
} from '../utils/shopHtmlInjector';
import { getHtmlPreviewCaption, getSectionBadgeLabel } from '../utils/shopSectionUi';

export interface ShopPreviewConfig {
    language: string;
    buyLabel: string;
    oldPrice: string;
    discountPercent: number;
    videoTitle: string;
    videoSubtitle: string;
    multiplier: string;
    unitPriceLabel: string;
}

export const DEFAULT_SHOP_PREVIEW_CONFIG: ShopPreviewConfig = {
    language: 'en',
    buyLabel: 'Buy now',
    oldPrice: '$19.99',
    discountPercent: 50,
    videoTitle: 'Watch & earn',
    videoSubtitle: 'Free betcoins reward',
    multiplier: '5X',
    unitPriceLabel: '1,000 B / $',
};

interface ShopHtmlPreviewProps {
    htmlContent: string;
    shopItem: ShopItemDto;
    titles: Localization;
    previewConfig: ShopPreviewConfig;
    effectCoef?: number;
    width?: number;
    showLabel?: boolean;
    showSectionBadge?: boolean;
    interactive?: boolean;
}

export function buildShopPreviewContext(
    shopItem: ShopItemDto,
    titles: Localization,
    previewConfig: ShopPreviewConfig,
    effectCoef?: number,
): ShopHtmlPreviewContext {
    const lang = previewConfig.language;
    const title = titles[lang] ?? titles.en ?? Object.values(titles)[0] ?? '';
    const displayLabelRaw = shopItem.displayLabel;
    let displayLabel = '';
    if (displayLabelRaw) {
        try {
            const parsed = JSON.parse(displayLabelRaw) as Record<string, string>;
            displayLabel = parsed[lang] ?? parsed.en ?? Object.values(parsed)[0] ?? '';
        } catch {
            displayLabel = displayLabelRaw;
        }
    }

    const betcoins = shopItem.instantCoins ?? shopItem.betcoins ?? 0;
    const offerExpiresAtMillis = shopItem.offerExpiresAt
        ? new Date(shopItem.offerExpiresAt).getTime()
        : undefined;

    return {
        iconUrl: shopItem.iconUrl,
        storeCode: shopItem.storeCode ?? 'preview_store_code',
        title,
        description: title,
        buyLabel: previewConfig.buyLabel,
        betcoins,
        visualPrice: shopItem.visualPrice ? `$${shopItem.visualPrice}` : '$0.99',
        oldPrice: previewConfig.oldPrice,
        discountPercent: previewConfig.discountPercent,
        displaySection: (shopItem.displaySection ?? 'COIN_PACKS') as ShopDisplaySection,
        badgeType: shopItem.badgeType,
        displayLabel,
        packageDays: shopItem.packageDays,
        effectCoef,
        effectDuration: shopItem.effectDuration,
        noAds: shopItem.noAds ?? false,
        offerExpiresAtMillis,
        popular: shopItem.badgeType === 'POPULAR',
        unitPriceLabel: previewConfig.unitPriceLabel,
        multiplier: previewConfig.multiplier,
        isVideoReward: shopItem.displaySection === 'FREE_REWARDS',
        isVideoRewardAvailable: true,
        videoTitle: previewConfig.videoTitle,
        videoSubtitle: previewConfig.videoSubtitle,
        language: lang,
        firstPurchaseEyebrow: localizedFirstPurchaseEyebrow(lang),
    };
}

export default function ShopHtmlPreview({
    htmlContent,
    shopItem,
    titles,
    previewConfig,
    effectCoef,
    width = 360,
    showLabel = true,
    showSectionBadge = false,
    interactive = true,
}: ShopHtmlPreviewProps) {
    const section = shopItem.displaySection ?? 'COIN_PACKS';
    const sectionBadge = getSectionBadgeLabel(section);

    const previewHtml = React.useMemo(() => {
        if (!htmlContent?.trim()) return '';
        const ctx = buildShopPreviewContext(shopItem, titles, previewConfig, effectCoef);
        return injectShopHtml(htmlContent, ctx);
    }, [htmlContent, shopItem, titles, previewConfig, effectCoef]);

    const height = shopHtmlCardHeight(shopItem.displaySection, width);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            {(showSectionBadge && sectionBadge) || showLabel ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    {showSectionBadge && sectionBadge ? (
                        <Chip
                            label={sectionBadge}
                            size="small"
                            color={section === 'FIRST_PURCHASE' ? 'warning' : 'default'}
                            variant={section === 'FIRST_PURCHASE' ? 'filled' : 'outlined'}
                        />
                    ) : null}
                    {showLabel ? (
                        <Typography variant="caption" color="text.secondary">
                            {getHtmlPreviewCaption(section)}
                        </Typography>
                    ) : null}
                </Stack>
            ) : null}
            <Box sx={{
                width,
                height,
                borderRadius: '16px',
                border: '2px solid #444',
                overflow: 'hidden',
                background: '#1a1a22',
            }}>
                {previewHtml ? (
                    <iframe
                        title="Shop HTML Preview"
                        srcDoc={previewHtml}
                        sandbox="allow-scripts"
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            display: 'block',
                            background: 'transparent',
                            pointerEvents: interactive ? 'auto' : 'none',
                        }}
                    />
                ) : (
                    <Box sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'action.hover',
                        color: 'text.disabled',
                    }}>
                        <Typography variant="body2" textAlign="center" sx={{ px: 3 }}>
                            No HTML content
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
